import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/format";
import { AdminHeader, Badge, Panel, Row, Table, borderStyle, inputCls } from "../ui";
import MediaPicker from "../MediaPicker";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

export default function ProductsAdmin() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [picker, setPicker] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/admin/products", { params: { search: q || undefined } })
      .then((r) => setRows(r.data.products)).catch(() => setRows([]));
  }, [q]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    try {
      const body = {
        name: edit.name, price: Number(edit.price || 0), sku: edit.sku,
        short_description: edit.short_description, description: edit.description,
        stock_status: edit.stock_status, published: edit.published, featured: edit.featured,
      };
      if (edit.image) body.images = [{ url: edit.image, alt: edit.name }];
      if (edit.id) await adminApi.put(`/admin/products/${edit.id}`, body);
      else await adminApi.post("/admin/products", { ...body, image: edit.image });
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this product?")) return;
    await adminApi.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div data-testid="admin-products">
      <AdminHeader title="Shop products" subtitle={rows ? `${rows.length} products for sale online` : ""}>
        <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5" style={borderStyle}>
          <Search className="h-4 w-4 gold-text" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-transparent text-sm outline-none" data-testid="admin-products-search" />
        </div>
        <button type="button" className="btn-gold" data-testid="admin-new-product-button"
          onClick={() => setEdit({ name: "", price: 0, sku: "", short_description: "", description: "", stock_status: "In stock", image: "", published: true, featured: false })}>
          <Plus className="h-4 w-4" /> New product
        </button>
      </AdminHeader>

      {edit && (
        <Panel className="mb-5">
          <div className="grid gap-4 lg:grid-cols-4">
            <div>
              <SmartImage src={edit.image} alt="" ratio="aspect-square" fit="contain" fallbackLabel={edit.name} />
              <button type="button" onClick={() => setPicker(true)} className="btn-outline-nq mt-2 w-full px-3 py-1.5 text-xs">Choose image</button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:col-span-3">
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Product name" className={inputCls} style={borderStyle} data-testid="product-name-input" />
              <input type="number" step="0.01" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="Price" className={inputCls} style={borderStyle} data-testid="product-price-input" />
              <input value={edit.sku || ""} onChange={(e) => setEdit({ ...edit, sku: e.target.value })} placeholder="SKU" className={inputCls} style={borderStyle} />
              <input value={edit.stock_status || ""} onChange={(e) => setEdit({ ...edit, stock_status: e.target.value })} placeholder="In stock" className={inputCls} style={borderStyle} />
              <textarea rows={2} value={edit.short_description || ""} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} placeholder="Short description" className={`${inputCls} sm:col-span-2`} style={borderStyle} />
              <textarea rows={4} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Full description" className={`${inputCls} sm:col-span-2`} style={borderStyle} />
              <div className="flex items-center gap-3 sm:col-span-2">
                <button type="button" onClick={save} className="btn-gold" data-testid="product-save-button"><Save className="h-4 w-4" /> Save</button>
                <button type="button" onClick={() => setEdit(null)} className="btn-outline-nq">Cancel</button>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {!rows ? <PageLoader /> : (
        <Table head={["", "Name", "SKU", "Price", "Stock", ""]}>
          {rows.map((p) => (
            <Row key={p.id} testid="admin-product-row">
              <td className="w-16 px-4 py-2"><div className="w-10"><SmartImage src={p.image} alt="" ratio="aspect-square" fit="contain" fallbackLabel={p.name} /></div></td>
              <td className="px-4 py-3 font-medium">{p.name}</td>
              <td className="px-4 py-3 font-mono text-xs text-[color:var(--nq-stone)]">{p.sku || "-"}</td>
              <td className="px-4 py-3 font-mono text-xs gold-text">{money(p.price)}</td>
              <td className="px-4 py-3"><Badge tone={/out of/i.test(p.stock_status || "") ? "red" : "green"}>{p.stock_status || "In stock"}</Badge></td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <button type="button" onClick={() => setEdit({ ...p, image: p.image })} className="btn-outline-nq px-2 py-1 text-xs" data-testid="admin-product-edit">Edit</button>
                  <button type="button" onClick={() => remove(p.id)} className="rounded p-1.5 hover:bg-white/10" data-testid="admin-product-delete">
                    <Trash2 className="h-4 w-4" style={{ color: "#e07a72" }} />
                  </button>
                </div>
              </td>
            </Row>
          ))}
        </Table>
      )}

      {picker && <MediaPicker onClose={() => setPicker(false)} onPick={(m) => { setEdit((v) => ({ ...v, image: m.url })); setPicker(false); }} />}
    </div>
  );
}
